import { Avatar, Box } from '@mui/material'
import { type IncomeApprovalSearchCriteria, IncomeApprovalSearchOptions } from 'Pages/IncomeApproval/IncomeApprovalSearchOptions'
import { useEffect, useState } from 'react'
import { generatePath, useNavigate } from 'react-router'
import * as AppRoutes from 'Pages/Routes'
import DataTable from 'Components/Table/DataTable'
import { Column, ColumnSort, ColumnType, type RowData } from 'Components/Table/DataTable/DataTable.model'
import { type PaginationProps, usePagination, type SearchCriteria } from 'hooks/use-pagination'
import { sendGet } from 'hooks/use-fetch'
import { useAccountId } from 'ContextProviders/CurrentAccount'
import { FormatDateValue } from 'core/Utilities'
import { ProgramVerificationChip } from 'Components/Program/ProgramVerificationChip'
import S3Avatar from 'Components/S3Image/S3Avatar'

export enum IncomeApprovalType {
  OverIncome = 'OverIncome',
  PriorityWaivers = 'PriorityWaivers'
}

interface IncomeApprovalListProps {
  type: IncomeApprovalType
}

export function IncomeApprovalList (props: IncomeApprovalListProps): JSX.Element {
  const [loading, setLoading] = useState(true)
  const nav = useNavigate()
  const [ias, setIAs] = useState<RowData[]>([])
  const [searchData, setSearchData] = useState<IncomeApprovalSearchCriteria>({
    approvalStatus: 'P',
    childName: '',
    serviceProviderID: -1
  })

  const childColumn = new Column('child', 'Child', ColumnType.CUSTOM)
  childColumn.customCellGenerator = row => {
    return <div style={{ whiteSpace: 'nowrap' }} >
      {row.s3Key === null
        ? <Avatar sx={{ backgroundColor: '#7136BC', display: 'inline-grid' }}>{String(row.childName)[0]}</Avatar>
        : <S3Avatar imageKey={row.s3Key} height='50px' width='50px' style={{ display: 'inline-grid' }} />}
      <span style={{ whiteSpace: 'nowrap', paddingLeft: '5px' }}>{row.childName}</span>
    </div>
  }

  const fplColumn = new Column('povertyPercent', 'FPL%', ColumnType.CUSTOM)
  fplColumn.customCellGenerator = row => {
    return <>{row.povertyPercent}%</>
  }
  const programColumn = new Column('programName', 'Program', ColumnType.CUSTOM)
  programColumn.customCellGenerator = row => {
    return <ProgramVerificationChip programName={row.programName} status={row.programStatus} showStatusName={true} compressed={true} />
  }
  const requestedByColumn = new Column('whenRequested', 'Requested By', ColumnType.CUSTOM)
  requestedByColumn.customCellGenerator = row => {
    return <>
      <div>{row.requestingUserName}</div>
      <div>{FormatDateValue(row.whenRequested)}</div>
    </>
  }
  const providerColumn = new Column('serviceProviderName', 'Provider Details', ColumnType.CUSTOM)
  providerColumn.customCellGenerator = row => {
    return <>
      <div>{row.serviceProviderName}</div>
      <div>{row.facilityName}</div>
    </>
  }

  const ageColumn = new Column('age', 'Age')
  const eligibilityFactorColumn = new Column('factors', 'Factors', ColumnType.CUSTOM)
  eligibilityFactorColumn.customCellGenerator = row => {
    return <ul className='tight-ul'>
      {row.eligibilityFactors.map((f: string) => {
        return <li>{f}</li>
      })}
    </ul>
  }

  const columns = [childColumn, fplColumn, programColumn, requestedByColumn, providerColumn, ageColumn, eligibilityFactorColumn]

  const account = useAccountId()

  useEffect(() => {
    const loadData = async (): Promise<void> => {
      const { response, error } = await sendGet(`/IncomeApproval/IncomeApprovalList?type=${props.type}`, {})
      if (error[0] === '' && response !== null) {
        setIAs(response)
      }

      setLoading(false)
      handleSearch(searchData)
    }
    void loadData()
  }, [account])

  const handleSearch = (newdata: IncomeApprovalSearchCriteria): void => {
    setSearchData(newdata)
    const fieldArray: SearchCriteria[] = []
    if (newdata.approvalStatus !== null) {
      fieldArray.push({
        field: 'approvalStatus',
        value: newdata.approvalStatus
      })
    }
    if (newdata.childName !== '') {
      fieldArray.push({
        field: 'childName',
        value: newdata.childName
      })
    }
    if (newdata.serviceProviderID !== -1) {
      fieldArray.push({
        field: 'serviceProviderID',
        value: String(newdata.serviceProviderID)
      })
    }
    pagination.setSearchFields(fieldArray)
  }

  const paginationProps: PaginationProps<RowData> = {
    rows: ias,
    initialSize: 5,
    pageSizeSteps: [5, 10]
  }
  const pagination = usePagination(paginationProps)

  const rowClick = (event: React.MouseEvent<HTMLTableRowElement, MouseEvent>, row: RowData): void => {
    const detailsPage = generatePath(AppRoutes.INCOME_APPROVAL_DETAILS, { id: row.id })
    nav(detailsPage)
  }

  return <>
    <IncomeApprovalSearchOptions type={props.type} current={searchData} onChange={handleSearch} />
    <Box sx={{ width: '100%', padding: '30px 20px', borderRadius: '16px', background: '#fff' }}>
      <DataTable
        page={pagination.page}
        hasCheckbox={false}
        name='oiaTable'
        columns={columns}
        rows={pagination.internalRows}
        totalRecords={pagination.recordCount}
        loading={loading}
        onSortChange={(col, sorts) => {
          pagination.onSortChange(col, sorts[0].order)
        }}
        onPageChange={pagination.handleChangePage}
        onRowsPerPageChange={pagination.handleChangeRowsPerPage}
        onRowClick={rowClick}
        initialColumnSorts={[new ColumnSort('fullName')]}
      />
    </Box>

  </>
}
